import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { DealQuickEdit } from '@/components/deals/DealNameLink';
import {
  labelOfSalesTrackerColumn,
  SALES_TRACKER_COLUMN_KEYS,
  SALES_TRACKER_LAYOUT_OPTIONS,
  SALES_TRACKER_LOCKED_KEYS,
  SALES_TRACKER_MANAGER_PINNED_KEYS,
  SALES_TRACKER_PINNED_KEYS,
  salesTrackerLockReason,
  SalesTrackerTable,
  SalesTrackerTableSkeleton,
} from '@/components/sales-tracker/SalesTrackerTable';
import { plannedLineOf } from '@/components/sales-tracker/combine-pursuits';
import { api, ApiError, type PlannedProductLine } from '@/lib/api';
import { currentCycle } from '@/lib/document-naming';
import { hasFilterMemory } from '@/lib/filter-memory/use-filter-memory';
import { boardParamsOf, parseProjectIds } from '@/lib/sales-tracker';
import type { Project, SalesTrackerDealLaneProject, SalesTrackerDealRow } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout } from '@/lib/use-column-layout';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useCycleProjectOptions, useSalesTracker } from '@/lib/use-sales-tracker';
import { useTrackerSheetLink } from '@/lib/use-tracker-sheet';
import { TrackerSheetPanel } from '@/components/tracker-sheet/TrackerSheetPanel';
import { Notice } from '@/components/ui/Notice';
import { ViewToggle as SharedViewToggle } from '@/components/ui/ViewToggle';
import {
  Button,
  ColumnManager,
  Combobox,
  EmptyState,
  Field,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
} from '@/components/ui';

/**
 * The sales tracker: one BAND per project in a cycle, one ROW per DEAL on it —
 * plus the Google Sheets mirror of the same data.
 *
 * ## 🔴 The rows are DEALS, not pursuits — owner's decision 2026-10-12
 *
 * *SUPERSEDED: "one ROW per company being pursued for it", the "Add a pursuit"
 * header control, the pursuit form modal, per-row edit/delete and the
 * combine-into-a-document flow.* The pursuit concept is dropped and nothing here
 * calls a pursuit endpoint. A deal shows in every project band its line items
 * name, and a deal naming no project sits in the trailing "No project" band.
 * "New deal" opens the full `DealFormModal`; a deal's stage is changed in place
 * with `PATCH /deals/{id}` `{ stageId }`.
 *
 * ## Gates
 *
 * Reading the page is still the `sales-tracker` module (server-enforced, the
 * `tasks`/`notes`/`targets` class). The two WRITES on this page are deal writes,
 * so they gate on `deals`: `canCreate('deals')` for "New deal" (withheld, not
 * disabled, when refused) and `canUpdate('deals')` for the stage cell (read-only
 * otherwise). The gate is the optimisation; the API's refusal is the guarantee.
 *
 * ## EVERYTHING the board is scoped by lives in the URL
 *
 * `?cycleId=N&projectId=&view=` — shareable, and one link renders the same for
 * two people. Column LAYOUT is deliberately NOT here: it is the reader's own
 * chrome, stored against the account.
 *
 * ⛔ **Every default is spelled by the key's ABSENCE and never written on
 * arrival**, except `cycleId`, which is seeded EXACTLY ONCE per mount from
 * `currentCycle()` — see `defaultCycleSettled`. Changing the cycle clears the
 * project filter, whose ids are cycle-scoped.
 *
 * ⚠️ **The heading is labelled from `board.cycle`, never from the picker.**
 *
 * No `<main>` and no header of its own beyond `PageHeader`: `AppShell` owns the
 * first.
 */

/**
 * The band "+" basket — owner 2026-10-23: ONE line per product the project
 * sells, in the project's own product order, each carrying EVERY city of the
 * project, quantity 1 and no typed price. Built by `plannedLineOf`, the one
 * seeded-row builder, so price (catalogue, city pro-rata), the inclusion preset
 * and the recommended discount are then applied by the deal grid exactly as for
 * a person picking product + project by hand.
 *
 * ⚠️ Every product, NOT filtered by `seedsLineItems` and NOT re-ordered travel
 * last — that flag and order belong to the company "+" flow
 * (`@/lib/company-project-lines`); the owner asked for the project's products.
 */
function bandSeedLines(project: Project): PlannedProductLine[] {
  const cityIds = project.cities.map((city) => city.id);
  const seen = new Set<number>();
  const lines: PlannedProductLine[] = [];
  for (const product of project.products) {
    // `f_project_products` is unique per pair; collapsed anyway — a repeat is a 409.
    if (seen.has(product.id)) continue;
    seen.add(product.id);
    lines.push({
      ...plannedLineOf({
        units: null,
        projectId: project.id,
        projectName: project.name,
        product,
      }),
      cityIds,
    });
  }
  return lines;
}

/** Copied from `TasksPage` rather than shared: six lines of layout. */
function ErrorBanner({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" className="mx-4 mt-3" title={title}>
      {children && <span className="[overflow-wrap:anywhere]">{children}</span>}
    </Notice>
  );
}

/**
 * The sheet's place, held while the mirror's address is still being read — only
 * when the reader has ASKED for the sheet. The header is `TrackerSheetPanel`'s,
 * verbatim; ⚠️ change both in one edit.
 */
function SheetPlaceholderPanel() {
  return (
    <Panel>
      <PanelHeader
        title="Google Sheets mirror"
        description="EMERGE rewrites this sheet automatically — edit the record in EMERGE, not here."
      />
      <div aria-busy className="border-b border-border p-4">
        <Skeleton className="h-[70vh] min-h-[360px] w-full" />
      </div>
    </Panel>
  );
}

/** Which layout the board is drawn in. The kanban view was dropped on 2026-09-13. */
type SalesTrackerView = 'table' | 'sheet';

/**
 * ⛔ **A POSITIVE TEST FOR `sheet`, NEVER A NEGATION OF `board`** — a stale
 * `?view=board` link lands on the TABLE, the default, and the key is inert.
 * ⚠️ `setView` has to agree with this: the default is the key's absence.
 */
function parseView(raw: string | null): SalesTrackerView {
  return raw === 'sheet' ? 'sheet' : 'table';
}

/** The first pill is this page's default view. */
const VIEW_OPTIONS: readonly { value: SalesTrackerView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'sheet', label: 'Sheet' },
];

function ViewToggle({
  view,
  onChange,
}: {
  view: SalesTrackerView;
  onChange: (next: SalesTrackerView) => void;
}) {
  return (
    <SharedViewToggle
      label="Sales tracker view"
      value={view}
      options={VIEW_OPTIONS}
      onChange={onChange}
    />
  );
}

export function SalesTrackerPage() {
  const { ability } = useAuth();

  /*
   * Computed with the other hooks rather than as an early return, so the hook
   * order cannot change with the reader's permissions.
   */
  const canRead = ability.canRead('sales-tracker');
  const canCreateDeal = ability.canCreate('deals');
  const canUpdateDeal = ability.canUpdate('deals');

  // No argument: the hook's first parameter is a DEFAULT INIT, not options.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();

  const cycleId = useMemo(() => {
    const raw = new URLSearchParams(searchKey).get('cycleId');
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }, [searchKey]);

  /*
   * `invalid` must not be dropped: `?projectId=1,abc` narrows on `[1]` and SAYS
   * SO, where forwarding the typo is a 422 that takes the whole board down.
   */
  const { ids: projectIds, invalid: invalidProjectIds } = useMemo(
    () => parseProjectIds(new URLSearchParams(searchKey).get('projectId')),
    [searchKey],
  );

  const requestedView = useMemo(
    () => parseView(new URLSearchParams(searchKey).get('view')),
    [searchKey],
  );

  /*
   * The mirror's addresses. ⚠️ The argument is not optional here: it defaults to
   * the CLIENT DELIVERABLES spreadsheet.
   */
  const sheet = useTrackerSheetLink('sales-tracker');
  const sheetPreviewUrl = sheet.loading ? null : sheet.previewLink;
  // The frame is the EDIT url; `?? sheetPreviewUrl` covers an older API.
  const sheetEmbedUrl = sheet.loading ? null : (sheet.link ?? sheetPreviewUrl);
  const canShowSheet = sheetPreviewUrl !== null;
  const view: SalesTrackerView = requestedView === 'sheet' && canShowSheet ? 'sheet' : 'table';
  const resolvingSheet = requestedView === 'sheet' && sheet.loading;

  const cycles = useCycleOptions();

  /*
   * The table's column layout, stored AGAINST THE ACCOUNT, owned here so the
   * `ColumnManager` and the table are the same controller. Called
   * unconditionally, above the permission early-return.
   */
  const columnLayout = useColumnLayout(
    'sales-tracker',
    SALES_TRACKER_COLUMN_KEYS,
    SALES_TRACKER_LOCKED_KEYS,
    SALES_TRACKER_PINNED_KEYS,
    SALES_TRACKER_LAYOUT_OPTIONS,
  );

  /*
   * ── The board opens on the cycle running TODAY ──────────────────────────
   *
   * The resolved id is written INTO the URL, once — that is what keeps the
   * filter CLEARABLE. `defaultCycleSettled` tells apart: first arrival (apply
   * the default), an explicit `?cycleId=N` (it wins), and a cleared filter (it
   * stays cleared). "Once" means once per mount.
   */
  const defaultCycleSettled = useRef(false);

  useEffect(() => {
    if (defaultCycleSettled.current) return;
    if (cycleId !== null) {
      defaultCycleSettled.current = true;
      return;
    }
    // Filter memory answered (owner 2026-10-16): a remembered current cycle is
    // already restored as today's, and a remembered clear stays cleared.
    if (hasFilterMemory('/sales-tracker', searchKey)) {
      defaultCycleSettled.current = true;
      return;
    }
    if (cycles.loading) return;
    defaultCycleSettled.current = true;
    if (cycles.failed) return;

    const running = currentCycle(cycles.cycles);
    if (!running) return;

    const next = new URLSearchParams(searchKey);
    next.set('cycleId', String(running.id));
    setSearchParams(next, { replace: true });
  }, [cycleId, cycles.loading, cycles.failed, cycles.cycles, searchKey, setSearchParams]);

  // A fresh object every render is safe: the hook keys off a serialised string.
  const boardParams = useMemo(
    () => (cycleId === null ? null : boardParamsOf({ cycleId, projectIds })),
    [cycleId, projectIds],
  );

  const {
    board: serverBoard,
    loading,
    refetching,
    error,
    reload,
    refreshQuietly,
  } = useSalesTracker(boardParams, canRead);

  // The server always appends the "No project" lane. Under a project filter it
  // answers a question nobody asked, so it is dropped here.
  const board = useMemo(
    () =>
      serverBoard === null || projectIds.length === 0
        ? serverBoard
        : { ...serverBoard, lanes: serverBoard.lanes.filter((lane) => lane.project !== null) },
    [serverBoard, projectIds],
  );

  // The filter's vocabulary comes from the PROJECTS MASTER, never `board.lanes`.
  const cycleProjects = useCycleProjectOptions(cycleId, canRead);

  const [newDealOpen, setNewDealOpen] = useState(false);

  /*
   * The band "+" (owner 2026-10-23). The lane carries no products or cities, so
   * the project is read first; the form opens once it lands, already seeded.
   * ⚠️ A second click while one is reading replaces it (the first is aborted).
   */
  const [seedLines, setSeedLines] = useState<PlannedProductLine[] | null>(null);
  const [seedPendingProjectId, setSeedPendingProjectId] = useState<number | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const seedAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => seedAbortRef.current?.abort(), []);

  const openNewDealForProject = (project: SalesTrackerDealLaneProject) => {
    seedAbortRef.current?.abort();
    const controller = new AbortController();
    seedAbortRef.current = controller;
    setSeedError(null);
    setSeedPendingProjectId(project.id);
    api
      .getProject(project.id, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setSeedPendingProjectId(null);
        setSeedLines(bandSeedLines(response.data));
        setNewDealOpen(true);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setSeedPendingProjectId(null);
        setSeedError(
          cause instanceof ApiError ? cause.message : 'The project didn’t load. Try again.',
        );
      });
  };

  const closeNewDeal = () => {
    setNewDealOpen(false);
    setSeedLines(null);
  };

  const cycleOptions = useMemo(() => {
    const options = cycles.cycles.map((cycle) => ({
      value: String(cycle.id),
      label: cycle.cycleName,
    }));
    if (cycleId !== null && !options.some((option) => option.value === String(cycleId))) {
      options.unshift({
        value: String(cycleId),
        label: board?.cycle?.name ?? `Cycle #${cycleId}`,
      });
    }
    return options;
  }, [cycles.cycles, cycleId, board]);

  /** ⚠️ `{ replace: true }` on EVERY write — a filter change is not a navigation. */
  function writeParams(mutate: (params: URLSearchParams) => void) {
    const params = new URLSearchParams(searchKey);
    mutate(params);
    setSearchParams(params, { replace: true });
  }

  function setCycle(next: string) {
    // Settled by hand too: cleared means cleared.
    defaultCycleSettled.current = true;
    writeParams((params) => {
      if (next === '') params.delete('cycleId');
      else params.set('cycleId', next);
      // The project filter is CYCLE-SCOPED, so it goes with the cycle.
      params.delete('projectId');
    });
  }

  function setProjectIds(values: readonly string[]) {
    // Every member re-validated: one malformed entry would 422 the whole board.
    const ids = values.flatMap((value) => {
      const parsed = Number(value);
      return Number.isSafeInteger(parsed) && parsed > 0 ? [parsed] : [];
    });
    writeParams((params) => {
      if (ids.length === 0) params.delete('projectId');
      else params.set('projectId', ids.join(','));
    });
  }

  function setView(next: SalesTrackerView) {
    // The TABLE is the default, spelled by the key's ABSENCE.
    writeParams((params) => {
      if (next === 'sheet') params.set('view', 'sheet');
      else params.delete('view');
    });
  }

  function clearLaneFilters() {
    writeParams((params) => {
      params.delete('projectId');
      // Scrubbed even though nothing reads it any more.
      params.delete('nonEmptyOnly');
      params.delete('groupBy');
    });
  }

  /**
   * One deal's stage, then a re-read of the board.
   *
   * 🔴 **It does not catch** — the stage cell renders the refusal beside the
   * control. ⚠️ **A re-read, not a splice**: the deal can sit in several bands,
   * and `counts.accepted` is quote-derived — the server restates both.
   * ⛔ The body is `{ stageId }` and nothing else.
   */
  async function updateStage(row: SalesTrackerDealRow, stageId: number) {
    await api.updateDeal(row.dealId, { stageId });
    reload();
  }

  /*
   * A selected id the master does not carry is INJECTED, labelled by id, so a
   * link naming it does not render an empty control beside a filtered board.
   */
  const projectFilterOptions = useMemo(() => {
    const options = cycleProjects.projects.map((project) => ({
      value: String(project.id),
      label: project.name.trim() === '' ? `Project #${project.id}` : project.name,
    }));
    const known = new Set(options.map((option) => option.value));
    const missing = projectIds
      .filter((id) => !known.has(String(id)))
      .map((id) => ({ value: String(id), label: `Project #${id}` }));
    return [...missing, ...options];
  }, [cycleProjects.projects, projectIds]);

  const projectFilterValues = useMemo(() => projectIds.map(String), [projectIds]);

  // Below every hook; the route carries no guard.
  if (!canRead) {
    return (
      <>
        <PageHeader eyebrow="Trackers" title="Sales Tracker" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not grant access to the sales tracker. Ask an administrator to change it if you need this screen."
          />
        </Panel>
      </>
    );
  }

  const resolvingDefaultCycle = cycleId === null && cycles.loading;
  const showSkeleton = resolvingDefaultCycle || (loading && !board);

  const cycleLabel = board?.cycle?.name ?? null;
  /** Project bands only — the trailing "No project" band is not a project. */
  const projectCount = board?.lanes.filter((lane) => lane.project !== null).length ?? 0;
  /** DISTINCT deals: one deal in three bands is one deal. */
  const dealCount = board
    ? new Set(board.lanes.flatMap((lane) => lane.deals.map((deal) => deal.dealId))).size
    : 0;

  const panelDescription = board
    ? cycleLabel === null
      ? 'That cycle no longer exists.'
      : `${cycleLabel} — ${projectCount} ${projectCount === 1 ? 'project' : 'projects'}` +
        (board.lanes.length > 0 && dealCount === 0 ? ', no deals yet' : '')
    : undefined;

  const laneFiltersActive = projectIds.length > 0;

  return (
    <>
      <PageHeader
        eyebrow="Trackers"
        title="Sales Tracker"
        actions={canShowSheet ? <ViewToggle view={view} onChange={setView} /> : undefined}
      />

      {resolvingSheet ? (
        <SheetPlaceholderPanel />
      ) : view === 'sheet' && sheetEmbedUrl !== null ? (
        <TrackerSheetPanel
          embedUrl={sheetEmbedUrl}
          openUrl={sheet.link}
          frameTitle="Sales tracker in Google Sheets"
        />
      ) : (
        <Panel aria-labelledby="sales-tracker-heading" className="min-w-0">
          <PanelHeader
            id="sales-tracker-heading"
            className="[&_h2]:font-medium [&_h2]:text-fg-muted"
            title="Sales Tracker"
            {...(panelDescription ? { description: panelDescription } : {})}
          />

          {/* The filter bank: its own wrapping row, never `PanelHeader`'s `actions`. */}
          <div className="flex flex-wrap items-end gap-x-3 gap-y-3 border-b border-border px-4 py-3">
            <div className="w-full min-w-0 sm:w-56">
              <Field label="Cycle">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={cycleId === null ? '' : String(cycleId)}
                    onChange={setCycle}
                    options={cycleOptions}
                    emptyOptionLabel="Choose a cycle…"
                    placeholder="Choose a cycle…"
                    searchPlaceholder="Search cycles…"
                    disabled={cycles.loading}
                  />
                )}
              </Field>
            </div>

            <div className="w-full min-w-0 sm:w-64">
              <Field label="Projects">
                {({ id, 'aria-describedby': describedBy }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    values={projectFilterValues}
                    onChange={setProjectIds}
                    options={projectFilterOptions}
                    loading={cycleProjects.loading}
                    disabled={cycleId === null}
                    placeholder="Every project"
                    searchPlaceholder="Search projects…"
                    summaryNoun={{ singular: 'project', plural: 'projects' }}
                    // A FILTER, so select-all beside a truncation footnote is safe.
                    selectAll="even-when-truncated"
                    {...(cycleProjects.hasMore
                      ? {
                          footnote: `Showing the first ${cycleProjects.projects.length} projects in this cycle`,
                        }
                      : {})}
                  />
                )}
              </Field>
            </div>

            {/*
             * The column manager, with "New deal" immediately beside it (owner,
             * 2026-10-12). ⚠️ "New deal" is WITHHELD, not disabled, from a reader
             * who may not create deals — a `title` on a disabled `Button` never
             * renders, so a disabled one could carry no reason.
             */}
            <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
              <ColumnManager
                className="shrink-0"
                layout={columnLayout}
                lockedKeys={SALES_TRACKER_LOCKED_KEYS}
                pinnedKeys={SALES_TRACKER_MANAGER_PINNED_KEYS}
                lockReasonOf={salesTrackerLockReason}
                labelOf={labelOfSalesTrackerColumn}
              />
              {canCreateDeal && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSeedLines(null);
                    setNewDealOpen(true);
                  }}
                >
                  New deal
                </Button>
              )}
            </div>
          </div>

          {cycles.failed && (
            <ErrorBanner title="Couldn’t load the cycles.">
              The picker is empty because the list didn’t load, not because there are none. Reload
              to try again.
            </ErrorBanner>
          )}

          {cycleProjects.failed && (
            <ErrorBanner title="Couldn’t load the projects.">
              The project filter is empty because the list didn’t load, not because this cycle has
              none. Reload to try again.
            </ErrorBanner>
          )}

          {invalidProjectIds.length > 0 && (
            <ErrorBanner title="Part of the project filter in this link isn’t usable.">
              {`Ignored ${invalidProjectIds.length === 1 ? 'this value' : 'these values'}: ${invalidProjectIds.join(', ')}. `}
              {projectIds.length > 0
                ? 'The board is filtered by the projects that were readable.'
                : 'Nothing was left to filter by, so every project is shown.'}
            </ErrorBanner>
          )}

          {error !== null && <ErrorBanner title="Couldn’t load the board.">{error}</ErrorBanner>}

          {seedError !== null && (
            <ErrorBanner title="Couldn’t open a new deal for that project.">{seedError}</ErrorBanner>
          )}

          {showSkeleton ? (
            <SalesTrackerTableSkeleton />
          ) : cycleId === null ? (
            <EmptyState
              title="Choose a cycle"
              description={
                cycles.failed
                  ? 'The cycle list didn’t load, so there is nothing to choose from yet. Reload to try again.'
                  : 'The tracker lists every deal in a cycle, grouped by project. Pick one above to see it.'
              }
            />
          ) : board === null ? (
            <EmptyState
              title="The tracker didn’t load"
              description="Nothing came back for this cycle. Reload to try again."
              action={
                <Button variant="secondary" onClick={reload}>
                  Try again
                </Button>
              }
            />
          ) : board.lanes.length === 0 ? (
            laneFiltersActive ? (
              <EmptyState
                title="No projects match the filter"
                description="The projects you picked are not on this cycle’s tracker. They may belong to another cycle."
                action={
                  <Button variant="secondary" onClick={clearLaneFilters}>
                    Clear the project filter
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No projects in this cycle"
                description={
                  cycleLabel === null
                    ? 'That cycle no longer exists. Choose another one above.'
                    : `${cycleLabel} has no projects yet. Add one on the Projects page.`
                }
              />
            )
          ) : (
            /*
             * A refetch DIMS the board rather than blanking it. `DealQuickEdit` is
             * the one deal form behind every deal-name link on the table (both
             * layouts); a save re-reads the board.
             */
            <div
              aria-busy={refetching}
              className={refetching ? 'opacity-60 transition-opacity duration-150' : undefined}
            >
              <DealQuickEdit onSaved={reload}>
                <SalesTrackerTable
                  board={board}
                  layout={columnLayout}
                  onUpdateStage={canUpdateDeal ? updateStage : undefined}
                  // A note written in the drawer lands on the company; refresh quietly.
                  onNotesChanged={refreshQuietly}
                  onNewDealForProject={canCreateDeal ? openNewDealForProject : undefined}
                  newDealPendingProjectId={seedPendingProjectId}
                />
              </DealQuickEdit>
            </div>
          )}
        </Panel>
      )}

      {/*
       * The full deal form, create mode. ⚠️ Re-gated on `canCreateDeal` as well
       * as on the button — a permission can be withdrawn under a live token.
       * A save closes it and re-reads the board, so the deal appears in every
       * band its lines name.
       */}
      {newDealOpen && canCreateDeal && (
        <DealFormModal
          open
          onClose={closeNewDeal}
          {...(seedLines === null ? {} : { prefill: { lines: seedLines } })}
          onSaved={() => {
            closeNewDeal();
            reload();
          }}
        />
      )}
    </>
  );
}
